import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'
import { useMediaQuery } from 'usehooks-ts'

import stylesLayout from '@pages/catalog/ui/styles.module.scss'

import { useAddressStore } from '@features/shipping-methods'


import { CategoriesBranch } from '@widgets/categories-branch'
import ProductsInfiniteScroll from '@widgets/products-infinite-scroll'


import { AllProductsPreviewCardQuery, AllProductsPreviewCardQueryVariables, Product, useAllProductsPreviewCardQuery, useSingleProductGroupQuery } from '@shared/generated'
import useFetchMorePagination from '@shared/hooks/useFetchMorePagination'
import { cn } from '@shared/lib/cn'
import Breadcrumbs from '@shared/ui/breadcrumbs'
import Container from '@shared/ui/container'
import PageGrid from '@shared/ui/page-grid'
import TitleWithCount from '@shared/ui/title-with-count'
import Wrapper from '@shared/ui/wrapper'
import getNoun from '@shared/uttils/getNoun'

import styles from './styles.module.scss'

const LIMIT = 12

export default function ProductGroupProductsPage () {
  const { t: tn } = useTranslation('navigation')
  const { t: tp } = useTranslation('product')

  const isTablet = useMediaQuery('(max-width: 991px)')
  const { shopId } = useAddressStore()
  const params = useParams()

  const productGroupId = params.productGroupId as string

  const { data: productGroupData } = useSingleProductGroupQuery({
    variables: {
      shop_id: shopId,
      id: productGroupId,
    }
  })

  const productGroupLabel = productGroupData?.singleProductGroup?.label ?? ''

  const { data, loading, fetchMore } = useAllProductsPreviewCardQuery({
    variables: {
      shopId,
      pager: {
        limit: LIMIT
      },
      filter: {
        fields: [ 'productGroupIds' ],
        op: 'contains',
        value: [ productGroupId ]
      }
    }
  })

  const { hasMore, loadingMore, fetchNextPage, clearPagination } =
    useFetchMorePagination<
      AllProductsPreviewCardQuery,
      AllProductsPreviewCardQueryVariables
    >({
      loading,
      fetchMore,
      pageVariable: 'pager',
      usePager: true,
      variables: {
        shopId,
        pager: { limit: LIMIT },
        filter: {
          fields: [ 'productGroupIds' ],
          op: 'contains',
          value: [ productGroupId ]
        }
      },
      updaterFunction (cached, fetched) {
        const prev = cached?.allProducts?.edges ?? []
        const curr = fetched?.allProducts?.edges ?? []

        return {
          hasMore: curr.length > 0,
          result: {
            ...cached,
            allProducts: {
              ...cached.allProducts,
              edges: [ ...prev, ...curr ],
            },
          } as AllProductsPreviewCardQuery,
        }
      },
    })

  
  
  useEffect(() => {
    clearPagination()
  }, [ clearPagination ])

  const totalProducts = data?.allProducts?.pageInfo?.total ?? 0

  return (
    <Container>
      <PageGrid>
        <Wrapper className={styles.wrapper}>
          <div className={stylesLayout.grid}>
            <Breadcrumbs
              className={stylesLayout.breadcrumbs}
              links={[
                {
                  title: tn('catalog'),
                  to: '/catalog',
                },
                {
                  title: productGroupData?.singleProductGroup?.label as string,
                },
              ]}
              style={{
                marginBottom: '24px',
              }}
            />
            {!isTablet && (
              <div className={cn(stylesLayout.aside, styles.aside)}>
                <div className={styles.aside}>
                  <CategoriesBranch className={styles.aside__content} />
                </div>
              </div>
            )}

            <div className={cn(stylesLayout.content)}>
              <div className={styles.root__top}>
                {productGroupLabel && (
                  <TitleWithCount
                    className={styles.root__title}
                    title={productGroupLabel}
                  >
                    {totalProducts !== null && (
                      <>
                        {totalProducts}{' '}
                        {getNoun(
                          totalProducts,
                          tp('product.1'),
                          tp('product.2'),
                          tp('product.3'),
                        )}
                      </>
                    )}
                  </TitleWithCount>
                )}
              </div>

              <ProductsInfiniteScroll
                products={data?.allProducts?.edges as Product[]}
                loading={loading}
                fetchNextPage={fetchNextPage}
                hasMore={hasMore}
                loadingMore={loadingMore}
              />
            </div>
          </div>
        </Wrapper>
       
      </PageGrid>
    </Container>
  )
}